import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, PaperPlaneTilt } from '@phosphor-icons/react';
import { useAuth } from '../../App';
import { hasProAccess } from '../../utils/trialHelpers';
import { NotifyContactSection, buildContactMessage } from './NotifyContactSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { email: false, sms: false, message: '', edited: false };

// Send an email / SMS update to the task's contact at any time
export const MessageContactModal = ({ task, contact, onClose, onSent }) => {
  const { user } = useAuth();
  const [value, setValue] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const template = useMemo(() => buildContactMessage({
    contactName: contact?.name,
    senderName: user?.name,
    title: task.title,
    dueDate: task.due_date ? String(task.due_date).slice(0, 10) : '',
    time: task.time,
    location: task.location,
  }), [contact?.name, user?.name, task]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const channels = [value.email && 'email', value.sms && 'sms'].filter(Boolean);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/tasks/${task.id}/notify-contact`, { channels, message: value.message }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const labels = { email: 'Email', sms: 'SMS' };
      let anyOk = false;
      Object.entries(data.results || {}).forEach(([ch, r]) => {
        if (r.ok) { anyOk = true; toast.success(`${labels[ch]} sent to ${contact?.name || 'the contact'}`); }
        else toast.error(`${labels[ch]} not sent: ${r.error}`);
      });
      if (anyOk) { onSent?.(); onClose(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not message the contact.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center bg-black/50 lg:p-6" onClick={() => !sending && onClose()} data-testid="message-contact-modal">
      <div className="w-full lg:max-w-xl bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-display text-lg font-semibold text-gray-900">Message {contact?.name || 'contact'}</h2>
          <button onClick={onClose} disabled={sending} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" data-testid="message-contact-close-btn"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <NotifyContactSection
            contact={contact}
            isPro={hasProAccess(user)}
            value={value}
            onChange={setValue}
            template={template}
            onUpgrade={() => toast.info('Messaging contacts is a Pro feature. Upgrade in Settings > Subscription.')}
            footnote="Email replies come to you."
          />
          {hasProAccess(user) && (
            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={sending} className="rounded-xl px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button
                onClick={send}
                disabled={sending || channels.length === 0 || !value.message.trim()}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 disabled:opacity-40"
                data-testid="message-contact-send-btn"
              >
                <PaperPlaneTilt size={16} weight="bold" />{sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageContactModal;
